import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, evalCondition } from "@/lib/api";
import { toast } from "sonner";
import { Scale, ArrowRight, ArrowLeft, Check, Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/24a9988c-9f50-4232-8b87-cdd4daf87c63/images/e0b3a0d3c13982bf0874ffd9834b737535bc03c45370cc23c3f715426b2ad45e.png";

export default function Intake() {
  const { id } = useParams();
  const nav = useNavigate();
  const [matter, setMatter] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [startedAt] = useState(Date.now());

  useEffect(() => {
    api.get(`/matters/${id}/questions`).then(({ data }) => {
      setQuestions(data.questions);
      setAnswers(data.answers || {});
    });
    api.get(`/matters/${id}`).then(({ data }) => setMatter(data));
  }, [id]);

  const visible = useMemo(() => questions.filter((q) => evalCondition(q.condition, answers)), [questions, answers]);
  const current = visible[idx];
  const progress = visible.length ? Math.round(((idx) / visible.length) * 100) : 0;

  const setAns = (val) => setAnswers((a) => ({ ...a, [current.id]: val }));

  const canContinue = () => {
    return true;
  };

  const hasAnswer = () => {
    const v = answers[current?.id];
    return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "";
  };

  const next = async () => {
    api.post(`/matters/${id}/intake-event`, { event_type: "intake_step", metadata: { question: current.id, index: idx } }).catch(() => {});
    if (idx + 1 < visible.length) {
      setIdx(idx + 1);
    } else {
      setSaving(true);
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      try {
        await api.patch(`/matters/${id}`, { intake_answers: answers, intake_complete: true, intake_progress: 100 });
        await api.post(`/matters/${id}/intake-event`, { event_type: "intake_time", metadata: { seconds } }).catch(() => {});
        toast.success("Intake complete — opening your claims workspace");
        nav(`/matter/${id}`);
      } catch {
        toast.error("Could not save. Please try again.");
        setSaving(false);
      }
    }
  };

  const back = () => (idx > 0 ? setIdx(idx - 1) : nav("/"));

  if (!current) return <div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen" style={{ background: "#fbfff4" }}>
      <header className="sticky top-0 z-40 overflow-hidden border-b" style={{ borderColor: "rgba(32,55,39,0.12)" }}>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundPosition: "center 40%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(18,30,22,0.72), rgba(18,30,22,0.5))" }} />
        </div>
        <div className="relative mx-auto flex h-20 max-w-3xl items-center justify-between px-6 text-white">
          <div className="flex items-center gap-2.5">
            <span className="font-serif text-xl font-semibold">AI Consumer Champion</span>
          </div>
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">{matter?.problem_label}</span>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-6 py-10">
        <div className="mb-8">
          <div className="mb-2 flex justify-between text-xs font-medium text-slate-500">
            <span>Question {idx + 1} of {visible.length}</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-1.5" data-testid="intake-progress" />
        </div>

        <div key={current.id} className="animate-fade-up">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-green-800">{current.section === "claimant" ? "Consumer" : current.section === "respondent" ? "Business" : "The dispute"}</span>
            {current.optional && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Optional</span>}
          </div>
          <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            {current.prompt}
          </h1>

          <div className="mt-8">
            {current.type === "single_select" && (
              <div className="grid gap-3">
                {current.options.map((opt) => {
                  const active = answers[current.id] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`intake-option-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => setAns(opt)}
                      className={cn("flex items-center justify-between rounded-xl border p-4 text-left transition-colors duration-150",
                        active ? "border-green-800 bg-green-50" : "border-slate-200 bg-white hover:border-slate-400")}
                    >
                      <span className="font-medium text-slate-800">{opt}</span>
                      {active && <Check className="h-5 w-5 text-green-800" />}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "multi_select" && (
              <div className="grid gap-3">
                {current.options.map((opt) => {
                  const arr = answers[current.id] || [];
                  const active = arr.includes(opt);
                  return (
                    <button
                      key={opt}
                      data-testid={`intake-multi-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => setAns(active ? arr.filter((x) => x !== opt) : [...arr, opt])}
                      className={cn("flex items-center justify-between rounded-xl border p-4 text-left transition-colors duration-150",
                        active ? "border-green-800 bg-green-50" : "border-slate-200 bg-white hover:border-slate-400")}
                    >
                      <span className="font-medium text-slate-800">{opt}</span>
                      <span className={cn("flex h-5 w-5 items-center justify-center rounded border", active ? "border-green-800 bg-green-800 text-white" : "border-slate-300")}>
                        {active && <Check className="h-3.5 w-3.5" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "text" && (
              <Input data-testid="intake-text-input" value={answers[current.id] || ""} placeholder={current.placeholder || ""}
                onChange={(e) => setAns(e.target.value)} className="h-12 text-base" autoFocus />
            )}

            {current.type === "textarea" && (
              <Textarea data-testid="intake-textarea" value={answers[current.id] || ""} placeholder={current.placeholder || ""}
                onChange={(e) => setAns(e.target.value)} rows={5} className="text-base" autoFocus />
            )}

            {current.type === "date" && (
              <Input data-testid="intake-date-input" type="date" value={answers[current.id] || ""}
                onChange={(e) => setAns(e.target.value)} className="h-12 w-full text-base sm:w-64" />
            )}
          </div>

          <div className="mt-5 flex gap-2.5 rounded-lg bg-slate-100/70 px-4 py-3 text-sm text-slate-600">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
            <span><span className="font-medium text-slate-700">Why we ask:</span> {current.why}</span>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Button data-testid="intake-back-btn" variant="ghost" onClick={back} className="rounded-full text-slate-500">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <Button data-testid="intake-next-btn" disabled={!canContinue() || saving} onClick={next}
              className="rounded-full bg-green-800 px-8 hover:bg-green-900">
              {idx + 1 === visible.length
                ? (saving ? "Finishing…" : "Finish")
                : (hasAnswer() ? "Continue" : "Skip")}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>

        <p className="mt-10 flex items-center gap-1.5 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Your answers stay in your private claims workspace. You can review and change them later.
        </p>
      </div>
    </div>
  );
}
